export type PhaseKey =
  | "filings"
  | "journal"
  | "chronology"
  | "suggestions"
  | "timeline"
  | "exhibits"
  | "discovery"
  | "attachments"
  | "calendar"
  | "forms"
  | "drafts"
  | "documents"
  | "insights"
  | "inbox"
  | "notifications"
  | "share"
  | "members"
  | "settings"
  | "exports"
  | "toolkits"
  | "scan"
  | "key-details"
  | "estimator";

export interface PhaseDefinition {
  key: PhaseKey;
  /** Path segment appended to /cases/:id. Use empty string for the index route. */
  path: string;
  label: string;
  /** Optional flag to suppress from navigation menus. Defaults to true. */
  showInMenu?: boolean;
  /** Name of the export to use; defaults to the module's default export. */
  exportName?: string;
  loader: () => Promise<Record<string, unknown>>;
}

export const PHASES: PhaseDefinition[] = [
  {
    key: "filings",
    path: "filings",
    label: "Filings",
    loader: () => import("@/pages/cases/CaseFilingsPage"),
  },
  {
    key: "journal",
    path: "journal",
    label: "Journal",
    loader: () => import("@/routes/cases/journal/JournalPageV2"),
  },
  {
    key: "chronology",
    path: "chronology",
    label: "Case History",
    loader: () => import("@/routes/cases/chronology/CaseHistoryPageV2"),
  },
  {
    key: "suggestions",
    path: "suggestions",
    label: "AI Suggestions",
    loader: () => import("@/routes/cases/suggestions/AiSuggestionsPage"),
    showInMenu: false,
  },
  {
    key: "timeline",
    path: "timeline",
    label: "Timeline",
    loader: () => import("@/routes/cases/timeline/TimelinePageV2"),
  },
  {
    key: "exhibits",
    path: "exhibits",
    label: "Exhibits",
    loader: () => import("@/routes/cases/exhibits/ExhibitsPageV2"),
  },
  {
    key: "discovery",
    path: "discovery",
    label: "Discovery",
    loader: () => import("@/routes/cases/discovery/DiscoveryPage"),
  },
  {
    key: "attachments",
    path: "attachments",
    label: "Attachments",
    showInMenu: false, // Now accessible via Insights > Attachments tab
    loader: () => import("@/pages/cases/CaseAttachmentsPage"),
  },
  {
    key: "calendar",
    path: "calendar",
    label: "Calendar",
    showInMenu: true, // T-51: Tasks & Calendar added to primary navigation
    loader: () => import("@/routes/cases/calendar/PlannerPageV2"),
  },
  {
    key: "forms",
    path: "forms",
    label: "Forms",
    loader: () => import("@/routes/cases/forms/FormsPageV2"),
  },
  {
    key: "drafts",
    path: "drafts",
    label: "Drafts",
    exportName: "DraftsPage",
    loader: () => import("@/routes/cases/drafts/DraftsPage"),
  },
  {
    key: "documents",
    path: "documents",
    label: "Documents",
    exportName: "DocumentsCenterPage",
    loader: () => import("@/routes/cases/documents/DocumentsCenterPage"),
  },
  {
    key: "insights",
    path: "insights",
    label: "Insights",
    showInMenu: false, // Utility page, not primary navigation
    exportName: "InsightsPage",
    loader: () => import("@/routes/cases/insights/InsightsPage"),
  },
  {
    key: "inbox",
    path: "inbox",
    label: "Inbox",
    loader: () => import("@/routes/cases/inbox/InboxPageV2"),
  },
  {
    key: "notifications",
    path: "notifications",
    label: "Notifications",
    loader: () => import("@/routes/cases/notifications/CaseNotificationsPage"),
    showInMenu: false,
  },
  {
    key: "share",
    path: "share",
    label: "Share",
      showInMenu: true, // WO7-P4-2: Share/Invite accessible from sidebar
    loader: () => import("@/routes/cases/share/SharePage"),
  },
  {
    key: "members",
    path: "members",
    label: "Members",
      showInMenu: true, // WO7-P4-2: Team management visible in sidebar
    loader: () => import("@/routes/cases/members/CaseMembersPage"),
  },
  {
    key: "settings",
    path: "settings",
    label: "Settings",
    showInMenu: false, // Case settings page
    loader: () => import("@/routes/cases/settings/CaseSettingsPage"),
  },
  {
    key: "exports",
    path: "exports",
    label: "Exports",
    showInMenu: true, // Practice-management handoff point
    loader: () => import("@/routes/cases/exports/ExportsPage"),
  },
  {
    key: "toolkits",
    path: "toolkits",
    label: "Toolkits",
    showInMenu: false, // Utility page, not primary navigation
    loader: () => import("@/routes/cases/toolkits/ToolkitsPage"),
  },
  {
    key: "scan",
    path: "scan",
    label: "Scan",
    showInMenu: false, // Utility page, not primary navigation
    loader: () => import("@/routes/cases/scan/ScanPageV2"),
  },
  {
    key: "key-details",
    path: "key-details",
    label: "Key Details",
    showInMenu: true, // SmartBox™ key details panel
    loader: () => import("@/pages/cases/CaseKeyDetailsPage"),
  },
  {
    key: "estimator",
    path: "estimator",
    label: "Case Estimator",
    showInMenu: false, // Gated: isPro + hasSmartBox
    loader: () => import("@/pages/cases/CaseEstimatorPage"),
  },
];
